import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Fade,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssistantIcon from '@mui/icons-material/Assistant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';

// Styled components
const RoleCard = styled(Card)(({ theme, selected }) => ({
  height: '100%',
  transition: 'all 0.3s ease',
  transform: selected ? 'translateY(-8px)' : 'none',
  border: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
  boxShadow: selected ? theme.shadows[8] : theme.shadows[1],
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: theme.shadows[5],
    transform: selected ? 'translateY(-8px)' : 'translateY(-4px)'
  }
}));

const RoleIcon = styled(Box)(({ theme, color = 'primary' }) => ({
  backgroundColor: theme.palette[color].main,
  color: theme.palette[color].contrastText,
  borderRadius: '50%',
  padding: theme.spacing(1.5),
  display: 'inline-flex',
  marginBottom: theme.spacing(2)
}));

const SelectedBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(0.5, 1.5),
  borderBottomLeftRadius: theme.shape.borderRadius,
  fontWeight: 'bold',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
}));

const FeatureList = styled(List)(({ theme }) => ({
  padding: 0,
  '& .MuiListItem-root': {
    padding: theme.spacing(0.5, 0)
  }
}));

const RoleSelector = ({ selectedRole, onRoleSelect }) => {
  // Role definitions with features and permissions
  const roles = [
    {
      id: 'coach',
      title: 'Coach',
      description: 'Full access to team management and recruiting tools',
      icon: <SportsSoccerIcon fontSize="large" />,
      color: 'primary',
      features: [
        'Manage team roster and scholarships',
        'Full recruiting tools and prospect management',
        'Advanced analytics and reporting',
        'Communication with prospects and team',
        'Trip planning and visit scheduling'
      ],
      popular: true
    },
    {
      id: 'recruiter',
      title: 'Recruiter',
      description: 'Focus on finding and evaluating prospects',
      icon: <PersonSearchIcon fontSize="large" />,
      color: 'secondary',
      features: [
        'Prospect search and evaluation',
        'Advanced athlete filtering',
        'Communication with prospects',
        'Trip planning and visit scheduling',
        'Limited team management access'
      ]
    },
    {
      id: 'admin',
      title: 'Administrator',
      description: 'Oversee the entire program with administrative tools',
      icon: <AdminPanelSettingsIcon fontSize="large" />,
      color: 'error',
      features: [
        'User management and permissions',
        'Program-wide analytics and reporting',
        'Budget and resource allocation',
        'System configuration and customization',
        'Access to all features and tools'
      ]
    },
    {
      id: 'assistant',
      title: 'Assistant',
      description: 'Support role with limited permissions',
      icon: <AssistantIcon fontSize="large" />,
      color: 'info',
      features: [
        'View team roster and prospects',
        'Basic communication tools',
        'Data entry and note-taking',
        'Calendar and scheduling access',
        'Limited reporting capabilities'
      ]
    }
  ];

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        Select your role
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose the role that best matches your position and responsibilities
      </Typography>
      
      <Grid container spacing={3}>
        {roles.map((role) => {
          const isSelected = selectedRole === role.id;
          
          return (
            <Grid item xs={12} sm={6} key={role.id}>
              <Fade in={true} timeout={300}>
                <RoleCard selected={isSelected}>
                  {isSelected && (
                    <SelectedBadge>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Selected
                      </Box>
                    </SelectedBadge>
                  )}
                  
                  <CardActionArea 
                    onClick={() => onRoleSelect(role.id)}
                    sx={{ height: '100%', p: 2 }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <RoleIcon color={role.color}>
                          {role.icon}
                        </RoleIcon>
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="h6" component="div">
                            {role.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {role.description}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {role.popular && (
                        <Chip 
                          label="Most Popular" 
                          color="primary" 
                          size="small" 
                          sx={{ mb: 2 }} 
                        />
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Features & Permissions
                      </Typography>
                      
                      <FeatureList>
                        {role.features.map((feature, index) => (
                          <ListItem key={index} disableGutters>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircleIcon 
                                fontSize="small" 
                                color={isSelected ? role.color : "action"} 
                              />
                            </ListItemIcon>
                            <ListItemText 
                              primary={feature} 
                              primaryTypographyProps={{ 
                                variant: 'body2',
                                color: isSelected ? 'text.primary' : 'text.secondary'
                              }} 
                            />
                          </ListItem>
                        ))}
                      </FeatureList>
                    </CardContent>
                  </CardActionArea>
                </RoleCard>
              </Fade>
            </Grid>
          );
        })}
      </Grid>
      
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedRole}
          onClick={() => onRoleSelect(selectedRole, true)}
        >
          Continue with {selectedRole ? roles.find(r => r.id === selectedRole).title : 'selected role'}
        </Button>
      </Box>
    </Box>
  );
};

export default RoleSelector;
